import loadable from "@loadable/component";
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
} from "react";
import {
  Params,
  Route,
  Routes,
  useNavigate,
  useParams,
} from "react-router-dom";

import { AppBaseLayout } from "./AppBaseLayout";
import { getAccount } from "./api/accounts";
import { useAppAccount } from "./components/AppProvider/AppAccountProvider";
import { useAppAuth } from "./components/AppProvider/AppAuthProvider";
import { useAppOrgsAndInvitedOrgs } from "./components/AppProvider/AppOrgsAndInvitedOrgsProvider";
import { TopNavOnlyLayout } from "./components/AppTopNav/TopNavOnlyLayout";
import { ErrorDisplay } from "./components/ErrorDisplay";
import { InOrganizationProvider } from "./components/InOrganizationProvider/Index";
import { ViewContextProvider } from "./components/ViewRenderer/ViewContextProvider";
import { ViewIdResolverContext } from "./components/ViewRenderer/ViewIdResolverProvider";
import { POST_LOGIN_REDIRECT_HASH } from "./firebase/helper";
import { checkIsMfaSettingRequired } from "./helpers/checkIsMfaSettingRequired";
import { buildOrgTopPath } from "./utils/orgTopPath";

import {
  AdminDeveloperProvider,
  AdminProvider,
} from "@/components/AdminProvider";
import { useAppCurrentVersion } from "./components/AppProvider/AppCurrentVersionProvider";
import { DatadogViewDispatcher } from "./datadog";
import { getLockOperationMessage } from "./domain/Version";
import { PageAdminFrontendMasters } from "./pages/PageAdminFrontendMasters";
import { PageAdminFrontendMastersCreate } from "./pages/PageAdminFrontendMastersCreate";
import { PageAdminFrontendMastersEdit } from "./pages/PageAdminFrontendMastersEdit";
import { PageAdminFrontendMastersVersionUpdate } from "./pages/PageAdminFrontendMastersVersionUpdate";

const PageInitialSetting = loadable(() =>
  import("./pages/PageInitialSetting").then((c) => c.PageInitialSetting)
);
const PageAccountSetting = loadable(() =>
  import("./pages/PageAccountSetting").then((c) => c.PageAccountSetting)
);
const PageOrganizations = loadable(() =>
  import("./pages/PageOrganizations").then((c) => c.PageOrganizations)
);
const PageOrganizationTop = loadable(() =>
  import("./pages/PageOrganizationTop").then((c) => c.PageOrganizationTop)
);
const PageOrganizationSetting = loadable(() =>
  import("./pages/PageOrganizationSetting").then(
    (c) => c.PageOrganizationSetting
  )
);
const PageVersionsSummaryComparison = loadable(() =>
  import("./pages/PageVersionsSummaryComparison").then(
    (c) => c.PageVersionsSummaryComparison
  )
);
const PageAlgorithmVersions = loadable(() =>
  import("./pages/PageAlgorithmVersions").then((c) => c.PageAlgorithmVersions)
);
const PageAlgorithmVersionCreate = loadable(() =>
  import("./pages/PageAlgorithmVersionCreate").then(
    (c) => c.PageAlgorithmVersionCreate
  )
);
const PageAlgorithmVersionEdit = loadable(() =>
  import("./pages/PageAlgorithmVersionEdit").then(
    (c) => c.PageAlgorithmVersionEdit
  )
);
const PageTadSheets = loadable(() =>
  import("./pages/PageTadSheets").then((c) => c.PageTadSheets)
);
const PageTadSheetCreate = loadable(() =>
  import("./pages/PageTadSheetCreate").then((c) => c.PageTadSheetCreate)
);
const PageTadSheetSetting = loadable(() =>
  import("./pages/PageTadSheetSetting").then((c) => c.PageTadSheetSetting)
);
const PageFrontendAddons = loadable(() =>
  import("./pages/PageFrontendAddons").then((c) => c.PageFrontendAddons)
);
const PageFrontendAddonCreate = loadable(() =>
  import("./pages/PageFrontendAddonCreate").then(
    (c) => c.PageFrontendAddonCreate
  )
);
const PageFrontendAddonEdit = loadable(() =>
  import("./pages/PageFrontendAddonEdit").then((c) => c.PageFrontendAddonEdit)
);
const PageFrontendAddonVersionUpdate = loadable(() =>
  import("./pages/PageFrontendAddonVersionUpdate").then(
    (c) => c.PageFrontendAddonVersionUpdate
  )
);
const PageVersionTop = loadable(() =>
  import("./pages/PageVersionTop").then((c) => c.PageVersionTop)
);
const PageVersionCreate = loadable(() =>
  import("./pages/PageVersionCreate").then((c) => c.PageVersionCreate)
);
const PageVersionInfo = loadable(() =>
  import("./pages/PageVersionInfo").then((c) => c.PageVersionInfo)
);
const PageSheet = loadable(() =>
  import("./pages/PageSheet").then((c) => c.PageSheet)
);
const PageSheetCreate = loadable(() =>
  import("./pages/PageSheetCreate").then((c) => c.PageSheetCreate)
);
const PageExtraPage = loadable(() =>
  import("./pages/PageExtraPage").then((c) => c.PageExtraPage)
);
const PageView = loadable(() =>
  import("./pages/PageView").then((c) => c.PageView)
);
const PageViewEdit = loadable(() =>
  import("./pages/PageViewEdit").then((c) => c.PageViewEdit)
);
const PageMembersInvite = loadable(() =>
  import("./pages/PageMembersInvite").then((c) => c.PageMembersInvite)
);
const PageUploadSheetSchema = loadable(() =>
  import("./pages/PageUploadSheetSchema").then((c) => c.PageUploadSheetSchema)
);
const PageUploadViews = loadable(() =>
  import("./pages/PageUploadViews").then((c) => c.PageUploadViews)
);
const PageAdminOrganizations = loadable(() =>
  import("./pages/PageAdminOrganizations").then((c) => c.PageAdminOrganizations)
);
const PageAdminViewMasters = loadable(() =>
  import("./pages/PageAdminViewMasters").then((c) => c.PageAdminViewMasters)
);
const PageAdminViewMaster = loadable(() =>
  import("./pages/PageAdminViewMaster").then((c) => c.PageAdminViewMaster)
);
const PageAdminAppConnectMasters = loadable(() =>
  import("./pages/PageAdminAppConnectMasters").then(
    (c) => c.PageAdminAppConnectMasters
  )
);
const PageAdminOrganizationSetting = loadable(() =>
  import("./pages/PageAdminOrganizationSetting").then(
    (c) => c.PageAdminOrganizationSetting
  )
);
const PageAdminMembersInvite = loadable(() =>
  import("./pages/PageAdminMembersInvite").then((c) => c.PageAdminMembersInvite)
);
const PageAdminOrganizationCreate = loadable(() =>
  import("./pages/PageAdminOrganizationCreate").then(
    (c) => c.PageAdminOrganizationCreate
  )
);
const PageAdminViewMasterCreate = loadable(() =>
  import("./pages/PageAdminViewMasterCreate").then(
    (c) => c.PageAdminViewMasterCreate
  )
);
const PageAdminAccounts = loadable(() =>
  import("./pages/PageAdminAccounts").then((c) => c.PageAdminAccounts)
);
const PageAdminLandingBucketFolders = loadable(() =>
  import("./pages/PageAdminLandingBucketFolders").then(
    (c) => c.PageAdminLandingBucketFolders
  )
);
const PageAdminAccountEdit = loadable(() =>
  import("./pages/PageAdminAccountEdit").then((c) => c.PageAdminAccountEdit)
);
const PageConnectMasters = loadable(() =>
  import("./pages/PageConnectMasters").then((c) => c.PageConnectMasters)
);
const PageConnects = loadable(() =>
  import("./pages/PageConnects").then((c) => c.PageConnects)
);
const PageConnectSetting = loadable(() =>
  import("./pages/PageConnectSetting").then((c) => c.PageConnectSetting)
);
const PageConnectCreate = loadable(() =>
  import("./pages/PageConnectCreate").then((c) => c.PageConnectCreate)
);
const PageConnectMasterDetail = loadable(() =>
  import("./pages/PageConnectMasterDetail").then(
    (c) => c.PageConnectMasterDetail
  )
);
const PageConnectMasterEdit = loadable(() =>
  import("./pages/PageConnectMasterEdit").then((c) => c.PageConnectMasterEdit)
);
const PageConnectMasterCreate = loadable(() =>
  import("./pages/PageConnectMasterCreate").then(
    (c) => c.PageConnectMasterCreate
  )
);
const PageAdminConnectMasterDetail = loadable(() =>
  import("./pages/PageAdminConnectMasterDetail").then(
    (c) => c.PageAdminConnectMasterDetail
  )
);
const PageAdminConnectMasterEdit = loadable(() =>
  import("./pages/PageAdminConnectMasterEdit").then(
    (c) => c.PageAdminConnectMasterEdit
  )
);
const PageAdminConnectMasterCreate = loadable(() =>
  import("./pages/PageAdminConnectMasterCreate").then(
    (c) => c.PageAdminConnectMasterCreate
  )
);
const PageAdminAppEndpointAddonMasters = loadable(() =>
  import("./pages/PageAdminAppEndpointAddonMasters").then(
    (c) => c.PageAdminAppEndpointAddonMasters
  )
);
const PageEndpointAddonMasterEdit = loadable(() =>
  import("./pages/PageEndpointAddonMasterEdit").then(
    (c) => c.PageEndpointAddonMasterEdit
  )
);
const PageEndpointAddonMasterCreate = loadable(() =>
  import("./pages/PageEndpointAddonMasterCreate").then(
    (c) => c.PageEndpointAddonMasterCreate
  )
);
const PageEndpointAddonMasters = loadable(() =>
  import("./pages/PageEndpointAddonMasters").then(
    (c) => c.PageEndpointAddonMasters
  )
);
const PageEndpointAddons = loadable(() =>
  import("./pages/PageEndpointAddons").then((c) => c.PageEndpointAddons)
);
const PageEndpointAddonCreate = loadable(() =>
  import("./pages/PageEndpointAddonCreate").then(
    (c) => c.PageEndpointAddonCreate
  )
);
const PageEndpointAddonSetting = loadable(() =>
  import("./pages/PageEndpointAddonSetting").then(
    (c) => c.PageEndpointAddonSetting
  )
);
const PageAdminAppPermissionMasters = loadable(() =>
  import("./pages/PageAdminAppPermissionMasters").then(
    (c) => c.PageAdminAppPermissionMasters
  )
);
const PageAdminAppPermissionMastersCreate = loadable(() =>
  import("./pages/PageAdminAppPermissionMastersCreate").then(
    (c) => c.PageAdminAppPermissionMastersCreate
  )
);
const PageAdminAppPermissionMastersEdit = loadable(() =>
  import("./pages/PageAdminAppPermissionMastersEdit").then(
    (c) => c.PageAdminAppPermissionMastersEdit
  )
);
const PageAdminCIMasters = loadable(() =>
  import("./pages/PageAdminCIMasters").then((c) => c.PageAdminCIMasters)
);
const PageAdminCIMastersCreate = loadable(() =>
  import("./pages/PageAdminCIMastersCreate").then(
    (c) => c.PageAdminCIMastersCreate
  )
);
const PageAdminCIMastersEdit = loadable(() =>
  import("./pages/PageAdminCIMastersEdit").then((c) => c.PageAdminCIMastersEdit)
);

/**
 * @description / のURLにアクセスしたときに誘導する先
 */
const RedirectToActualTop: React.FC = () => {
  const navigate = useNavigate();
  const appAuth = useAppAuth();
  const { account } = useAppAccount();
  const {
    organizations,
    isLoadedOrganizations,
    runReloadOrganizations,
    runReloadInvitedOrganizations,
  } = useAppOrgsAndInvitedOrgs();
  const initProcess = async () => {
    if (!appAuth.user) return;
    const isMfaSettingRequired = checkIsMfaSettingRequired(
      account ||
        (await getAccount({
          uid: appAuth.user.uid,
        }))
    );
    // MFA未設定はセキュリティ上のハードゲートなので必ず初期設定画面へ。
    // 退避先(stored)はここでは取得・削除しない。MFA設定完了後に再ログインで /
    // へ戻ってきた際、改めてこの関数で退避先へ遷移できるようにするため。
    if (isMfaSettingRequired) {
      navigate(`/initial_setting`);
      return;
    }

    // MFAを満たしていれば、退避したディープリンクを優先して消費する。
    // /organizations は defaultOrgId を要求しないため、初回招待(既定組織未設定)でも
    // 招待タブ等へ着地させたい。
    const stored = sessionStorage.getItem(POST_LOGIN_REDIRECT_HASH);
    if (stored) {
      sessionStorage.removeItem(POST_LOGIN_REDIRECT_HASH);
      navigate(stored); // 例: "/organizations?tab=invited"
      // 既ログイン中に受け取った招待などアプリ起動時の一覧では古い可能性があるため、
      // 遷移先で最新を表示できるよう組織一覧を再取得する(全画面ローディングを挟む)。
      await Promise.all([
        runReloadOrganizations(),
        runReloadInvitedOrganizations(),
      ]);
      return;
    }

    // ディープリンクが無い通常フロー: デフォルト組織が未設定なら初期設定画面へ
    if (!appAuth.user.defaultOrgId) {
      navigate(`/initial_setting`);
      return;
    }
    // 遷移先は自分のデフォルト組織(=所属組織)なので所属組織一覧に含まれる。
    if (!isLoadedOrganizations) return;
    const orgId = appAuth.user.defaultOrgId;
    const item = organizations.find((o) => o.org_id === orgId);
    navigate(buildOrgTopPath(orgId, item?.initial_extra_page_id));
  };
  useEffect(() => {
    initProcess();
  }, [isLoadedOrganizations]);
  return null;
};

const PageInitialSettingWithRedirectHandler: React.FC = () => {
  const navigate = useNavigate();
  const appAuth = useAppAuth();
  const { organizations, isLoadedOrganizations } = useAppOrgsAndInvitedOrgs();
  const { account } = useAppAccount();
  const [isLoading, setIsLoading] = React.useState(true);

  const initProcess = async () => {
    if (!appAuth.user) return;
    const isMfaSettingRequired = checkIsMfaSettingRequired(
      account ||
        (await getAccount({
          uid: appAuth.user.uid,
        }))
    );
    if (
      (!isMfaSettingRequired && appAuth.user?.defaultOrgId) ||
      organizations.length !== 0
    ) {
      if (!isLoadedOrganizations) return;
      const orgId = appAuth.user?.defaultOrgId ?? "";
      const item = organizations.find((o) => o.org_id === orgId);
      navigate(buildOrgTopPath(orgId, item?.initial_extra_page_id));
      return;
    }
    setIsLoading(false);
  };
  useEffect(() => {
    initProcess();
  }, [isLoadedOrganizations]);
  return isLoading ? null : <PageInitialSetting />;
};

/**
 * 初回MFAが未設定の場合は initial_setting へリダイレクトする
 * @param children
 * @constructor
 */
const InitialMFASettingRedirectHandler: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { account } = useAppAccount();
  const navigate = useNavigate();
  const appAuth = useAppAuth();
  const [isLoading, setIsLoading] = React.useState(true);

  const initProcess = async () => {
    if (!appAuth.user) return;
    const isMfaSettingRequired = checkIsMfaSettingRequired(
      account ||
        (await getAccount({
          uid: appAuth.user.uid,
        }))
    );
    if (isMfaSettingRequired) {
      navigate("/initial_setting");
      return;
    }
    setIsLoading(false);
  };
  useEffect(() => {
    initProcess();
  }, []);

  return isLoading ? null : <>{children}</>;
};

/**
 * 初回組織設定が未完了の場合は initial_setting へリダイレクトする
 * @param children
 * @constructor
 */
const InitialOrganizationSettingRedirectHandler: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const navigate = useNavigate();
  const appAuth = useAppAuth();
  const [isLoading, setIsLoading] = React.useState(true);
  const { organizations, isLoadedOrganizations } = useAppOrgsAndInvitedOrgs();

  const initProcess = async () => {
    if (!appAuth.user) return;
    if (organizations.length === 0) {
      navigate("/initial_setting");
      return;
    }
    setIsLoading(false);
  };
  useEffect(() => {
    if (isLoadedOrganizations) {
      initProcess();
    }
  }, [isLoadedOrganizations]);

  return isLoading ? null : <>{children}</>;
};

export const useAppRouteParams = () => {
  /**
   * 現在のURLパラメータを取得する
   */
  const params = useParams<
    | "organization_id"
    | "version_id"
    | "sheet_id"
    | "user_id"
    | "uid"
    | "account_id"
    | "view_id"
    | "algorithm_version_id"
    | "view_master_id"
    | "connect_id"
    | "frontend_addon_id"
    | "connect_master_id"
    | "endpoint_addon_master_id"
    | "endpoint_addon_id"
    | "member_id"
    | "version_category_id"
    | "connect_master_id"
    | "role_id"
    | "extra_page_id"
    | "tad_sheet_id"
  >() as Required<
    Params<
      | "organization_id"
      | "version_id"
      | "sheet_id"
      | "user_id"
      | "uid"
      | "account_id"
      | "view_id"
      | "algorithm_version_id"
      | "view_master_id"
      | "connect_id"
      | "frontend_addon_id"
      | "connect_master_id"
      | "endpoint_addon_master_id"
      | "endpoint_addon_id"
      | "member_id"
      | "version_category_id"
      | "connect_master_id"
      | "role_id"
      | "extra_page_id"
      | "tad_sheet_id"
    >
  >;
  // NOTE: ビュー配下のルートでは、URLのview_id（alias or ドキュメントID）を
  // ドキュメントIDへ解決する。それ以外のルートでは恒等変換（生の値をそのまま返す）。
  const resolveViewId = useContext(ViewIdResolverContext);
  return useMemo(
    () => ({
      organizationId: params.organization_id!,
      versionId: params.version_id!,
      sheetId: params.sheet_id!,
      userId: params.user_id!,
      uid: params.uid!,
      accountId: params.account_id!,
      viewId: resolveViewId(params.view_id!),
      algorithmVersionId: params.algorithm_version_id!,
      viewMasterId: params.view_master_id!,
      connectId: params.connect_id!,
      frontendAddonId: params.frontend_addon_id!,
      connectMasterId: params.connect_master_id!,
      endpointAddonMasterId: params.endpoint_addon_master_id!,
      endpointAddonId: params.endpoint_addon_id!,
      memberId: params.member_id!,
      versionCategoryId: params.version_category_id!,
      roleId: params.role_id!,
      extraPageId: params.extra_page_id!,
      tadSheetId: params.tad_sheet_id!,
    }),
    [params, resolveViewId]
  );
};

/**
 * 専用UI機能がOFFの場合は バージョントップなど へリダイレクトする
 * @param children
 * @constructor
 */
const EnableSheetRedirectHandler: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const navigate = useNavigate();
  const { organizations, isLoadedOrganizations } = useAppOrgsAndInvitedOrgs();
  const { versionId, organizationId } = useAppRouteParams();
  const organization = organizations.find(
    (org) => org.org_id === organizationId
  );
  const [isLoading, setIsLoading] = React.useState(true);

  const initProcess = async () => {
    if (organization?.is_sheet_enabled !== true) {
      if (versionId) {
        navigate(`/organizations/${organizationId}/versions/${versionId}`);
      } else {
        navigate(`/organizations/${organizationId}`);
      }
      return;
    }
    setIsLoading(false);
  };
  useEffect(() => {
    if (isLoadedOrganizations) {
      initProcess();
    }
  }, [isLoadedOrganizations]);

  return isLoading ? null : <>{children}</>;
};

/**
 * バージョンへの操作不可の場合は バージョントップ へリダイレクトする
 * @param children
 * @constructor
 */
const LockVersionOperationRedirectHandler: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const navigate = useNavigate();
  const { versionId, organizationId } = useAppRouteParams();
  const { orgServiceType } = useAppOrgsAndInvitedOrgs();
  const version = useAppCurrentVersion().version;
  const [isLoading, setIsLoading] = React.useState(true);

  const initProcess = async () => {
    if (getLockOperationMessage(version, orgServiceType)) {
      navigate(`/organizations/${organizationId}/versions/${versionId}`);
      return;
    }
    setIsLoading(false);
  };
  useEffect(() => {
    if (version) {
      initProcess();
    }
  }, [version]);

  return isLoading ? null : <>{children}</>;
};

/**
 * Admin権限がなければ、バージョントップ へリダイレクトする
 * @param children
 * @constructor
 */
const AdminViewRedirectHandler: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const navigate = useNavigate();
  const { isAAAdminOrAbove, isLoadedAccount, account } = useAppAccount();
  const { organizations, isLoadedOrganizations } = useAppOrgsAndInvitedOrgs();
  const [isLoading, setIsLoading] = React.useState(true);

  const initProcess = async () => {
    if (!isLoadedAccount) return;
    if (isAAAdminOrAbove !== true) {
      if (account?.default_org_id) {
        if (!isLoadedOrganizations) return;
        const orgId = account.default_org_id;
        const item = organizations.find((o) => o.org_id === orgId);
        navigate(buildOrgTopPath(orgId, item?.initial_extra_page_id));
        return;
      }
      navigate("/initial_setting");
      return;
    }
    setIsLoading(false);
  };
  useEffect(() => {
    if (isLoadedAccount) {
      initProcess();
    }
  }, [isLoadedAccount, isLoadedOrganizations]);

  return isLoading ? null : <>{children}</>;
};

/**
 * aa:admin以上 または aa:developer であれば通過。
 * それ以外はバージョントップへリダイレクトする。
 * aa:developer は /admin/accounts のみアクセス可能。
 */
const AdminDeveloperViewRedirectHandler: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const navigate = useNavigate();
  const { isAAAdminOrAbove, isAADeveloperOrAbove, isLoadedAccount, account } =
    useAppAccount();
  const { organizations, isLoadedOrganizations } = useAppOrgsAndInvitedOrgs();
  const [isLoading, setIsLoading] = React.useState(true);

  const initProcess = async () => {
    if (!isLoadedAccount) return;
    if (isAAAdminOrAbove !== true && isAADeveloperOrAbove !== true) {
      if (account?.default_org_id) {
        if (!isLoadedOrganizations) return;
        const orgId = account.default_org_id;
        const item = organizations.find((o) => o.org_id === orgId);
        navigate(buildOrgTopPath(orgId, item?.initial_extra_page_id));
        return;
      }
      navigate("/initial_setting");
      return;
    }
    setIsLoading(false);
  };
  useEffect(() => {
    if (isLoadedAccount) {
      initProcess();
    }
  }, [isLoadedAccount, isLoadedOrganizations]);

  return isLoading ? null : <>{children}</>;
};

type RedirectHandlerType =
  | "MFASetting"
  | "AdminView"
  | "AdminDeveloperView"
  | "OrganizationSetting"
  | "EnableSheet"
  | "LockVersionOperation";

/**
 * ルートの機能グループ。ルート分類の情報源で useAppRouteGroup() で取得する。
 * 認可ガードの redirectHandlers とは別の関心事。
 */
export type AppRouteGroup =
  | "admin"
  | "organizationSetting"
  | "algorithmVersion"
  | "frontendAddon"
  | "connect"
  | "endpointAddon"
  | "tadSheet";

type AppRoutesDefine = {
  [key: string]: {
    path: string;
    searchParams?: {
      [key: string]: {
        key: string;
        values?: {
          [key: string]: string;
        };
      };
    };
    redirectHandlers?: readonly RedirectHandlerType[];
    routeGroup?: AppRouteGroup;
    element: ReactNode;
  };
};
export const appRoutesDefine = {
  initialSetting: {
    path: "/initial_setting",
    element: <PageInitialSettingWithRedirectHandler />,
  },
  accountSetting: {
    path: "/account_setting",
    redirectHandlers: ["MFASetting"],
    element: <PageAccountSetting />,
  },
  adminOrganizations: {
    path: "/admin/organizations",
    redirectHandlers: ["MFASetting", "AdminView"],
    routeGroup: "admin",
    element: <PageAdminOrganizations />,
  },
  adminViewMasters: {
    path: "/admin/viewmasters",
    redirectHandlers: ["MFASetting", "AdminView"],
    routeGroup: "admin",
    element: <PageAdminViewMasters />,
  },
  adminViewMaster: {
    path: "/admin/viewmasters/:view_master_id",
    redirectHandlers: ["MFASetting", "AdminView"],
    routeGroup: "admin",
    element: <PageAdminViewMaster />,
  },
  adminFrontendMasters: {
    path: "/admin/frontendmasters",
    redirectHandlers: ["MFASetting", "AdminView"],
    routeGroup: "admin",
    element: <PageAdminFrontendMasters />,
  },
  adminFrontendMastersCreate: {
    path: "/admin/frontendmasters/create",
    redirectHandlers: ["MFASetting", "AdminView"],
    routeGroup: "admin",
    element: <PageAdminFrontendMastersCreate />,
  },
  adminFrontendMastersEdit: {
    path: "/admin/frontendmasters/:frontend_addon_id/frontend_addon_edit",
    redirectHandlers: ["MFASetting", "AdminView"],
    routeGroup: "admin",
    element: <PageAdminFrontendMastersEdit />,
  },
  adminFrontendMastersVersionUpdate: {
    path: "/admin/frontendmasters/:frontend_addon_id/frontend_addon_version_update",
    redirectHandlers: ["MFASetting", "AdminView"],
    routeGroup: "admin",
    element: <PageAdminFrontendMastersVersionUpdate />,
  },
  adminAppConnectMasters: {
    path: "/admin/connect_masters",
    redirectHandlers: ["MFASetting", "AdminView"],
    routeGroup: "admin",
    element: <PageAdminAppConnectMasters />,
  },
  adminConnectMasterDetail: {
    path: "/admin/connect_masters/:connect_master_id",
    redirectHandlers: ["MFASetting", "AdminView"],
    routeGroup: "admin",
    element: <PageAdminConnectMasterDetail />,
  },
  adminConnectMasterEdit: {
    path: "/admin/connect_masters/:connect_master_id/edit",
    redirectHandlers: ["MFASetting", "AdminView"],
    routeGroup: "admin",
    element: <PageAdminConnectMasterEdit />,
  },
  adminConnectMasterCreate: {
    path: "/admin/connect_masters/create",
    redirectHandlers: ["MFASetting", "AdminView"],
    routeGroup: "admin",
    element: <PageAdminConnectMasterCreate />,
  },
  adminAppEndpointAddonMasters: {
    path: "/admin/endpoint_addon_masters",
    redirectHandlers: ["MFASetting", "AdminView"],
    routeGroup: "admin",
    element: <PageAdminAppEndpointAddonMasters />,
  },
  adminEndpointAddonMasterEdit: {
    path: "/admin/endpoint_addon_masters/:endpoint_addon_master_id/edit",
    redirectHandlers: ["MFASetting", "AdminView"],
    routeGroup: "admin",
    element: <PageEndpointAddonMasterEdit />,
  },
  adminEndpointAddonMasterCreate: {
    path: "/admin/endpoint_addon_masters/create",
    redirectHandlers: ["MFASetting", "AdminView"],
    routeGroup: "admin",
    element: <PageEndpointAddonMasterCreate />,
  },
  adminAppPermissionMasters: {
    path: "/admin/permission_masters",
    redirectHandlers: ["MFASetting", "AdminView"],
    routeGroup: "admin",
    element: <PageAdminAppPermissionMasters />,
  },
  adminAppPermissionMastersCreate: {
    path: "/admin/permission_masters/create",
    redirectHandlers: ["MFASetting", "AdminView"],
    routeGroup: "admin",
    element: <PageAdminAppPermissionMastersCreate />,
  },
  adminAppPermissionMastersEdit: {
    path: "/admin/permission_masters/:app_permission_master_id/edit",
    redirectHandlers: ["MFASetting", "AdminView"],
    routeGroup: "admin",
    element: <PageAdminAppPermissionMastersEdit />,
  },
  adminCIMasters: {
    path: "/admin/ci_masters",
    redirectHandlers: ["MFASetting", "AdminView"],
    routeGroup: "admin",
    element: <PageAdminCIMasters />,
  },
  adminCIMastersCreate: {
    path: "/admin/ci_masters/create",
    redirectHandlers: ["MFASetting", "AdminView"],
    routeGroup: "admin",
    element: <PageAdminCIMastersCreate />,
  },
  adminCIMastersEdit: {
    path: "/admin/ci_masters/:ci_master_id/edit",
    redirectHandlers: ["MFASetting", "AdminView"],
    routeGroup: "admin",
    element: <PageAdminCIMastersEdit />,
  },
  adminOrganizationSetting: {
    path: "/admin/organizations/:organization_id",
    redirectHandlers: ["MFASetting", "AdminView"],
    routeGroup: "admin",
    element: <PageAdminOrganizationSetting tabId="organization_info" />,
  },
  adminOrganizationSettingOrganizationInfo: {
    path: "/admin/organizations/:organization_id/organization_info",
    redirectHandlers: ["MFASetting", "AdminView"],
    routeGroup: "admin",
    element: <PageAdminOrganizationSetting tabId="organization_info" />,
  },
  adminOrganizationSettingFunctions: {
    path: "/admin/organizations/:organization_id/functions",
    redirectHandlers: ["MFASetting", "AdminView"],
    routeGroup: "admin",
    element: <PageAdminOrganizationSetting tabId="functions" />,
  },
  adminOrganizationSettingMembers: {
    path: "/admin/organizations/:organization_id/members",
    redirectHandlers: ["MFASetting", "AdminView"],
    routeGroup: "admin",
    element: <PageAdminOrganizationSetting tabId="members" />,
  },
  adminOrganizationMembersInvite: {
    path: "/admin/organizations/:organization_id/members/invite",
    redirectHandlers: ["MFASetting", "AdminView"],
    routeGroup: "admin",
    element: <PageAdminMembersInvite />,
  },
  adminOrganizationSettingMembersEdit: {
    path: "/admin/organizations/:organization_id/members/:member_id/edit",
    redirectHandlers: ["MFASetting", "AdminView"],
    routeGroup: "admin",
    element: <PageAdminOrganizationSetting tabId="members" />,
  },
  adminOrganizationSettingVersionCategories: {
    path: "/admin/organizations/:organization_id/version_categories",
    redirectHandlers: ["MFASetting", "AdminView"],
    routeGroup: "admin",
    element: <PageAdminOrganizationSetting tabId="version_categories" />,
  },
  adminVersionCategoryEdit: {
    path: "/admin/organizations/:organization_id/version_categories/:version_category_id/edit",
    redirectHandlers: ["MFASetting", "AdminView"],
    routeGroup: "admin",
    element: <PageAdminOrganizationSetting tabId="version_categories" />,
  },
  adminVersionCategoryCreate: {
    path: "/admin/organizations/:organization_id/version_categories/create",
    redirectHandlers: ["MFASetting", "AdminView"],
    routeGroup: "admin",
    element: <PageAdminOrganizationSetting tabId="version_categories" />,
  },
  adminVersionCategorySort: {
    path: "/admin/organizations/:organization_id/version_categories/sort",
    redirectHandlers: ["MFASetting", "AdminView"],
    routeGroup: "admin",
    element: <PageAdminOrganizationSetting tabId="version_categories" />,
  },
  adminOrganizationSettingRoles: {
    path: "/admin/organizations/:organization_id/roles",
    redirectHandlers: ["MFASetting", "AdminView"],
    routeGroup: "admin",
    element: <PageAdminOrganizationSetting tabId="roles" />,
  },
  adminOrganizationSettingRolesEdit: {
    path: "/admin/organizations/:organization_id/roles/:role_id/edit",
    redirectHandlers: ["MFASetting", "AdminView"],
    routeGroup: "admin",
    element: <PageAdminOrganizationSetting tabId="roles" />,
  },
  adminOrganizationSettingRolesCreate: {
    path: "/admin/organizations/:organization_id/roles/create",
    redirectHandlers: ["MFASetting", "AdminView"],
    routeGroup: "admin",
    element: <PageAdminOrganizationSetting tabId="roles" />,
  },
  adminOrganizationSettingViews: {
    path: "/admin/organizations/:organization_id/views",
    redirectHandlers: ["MFASetting", "AdminView"],
    routeGroup: "admin",
    element: <PageAdminOrganizationSetting tabId="views" />,
  },
  adminOrganizationSettingNotification: {
    path: "/admin/organizations/:organization_id/notification",
    redirectHandlers: ["MFASetting", "AdminView"],
    routeGroup: "admin",
    element: <PageAdminOrganizationSetting tabId="notification" />,
  },
  adminOrganizationSettingCliApiKeys: {
    path: "/admin/organizations/:organization_id/cli_api_keys",
    redirectHandlers: ["MFASetting", "AdminView"],
    routeGroup: "admin",
    element: <PageAdminOrganizationSetting tabId="cli_api_keys" />,
  },
  adminAccounts: {
    path: "/admin/accounts",
    redirectHandlers: ["MFASetting", "AdminDeveloperView"],
    routeGroup: "admin",
    element: <PageAdminAccounts />,
  },
  adminLandingBucketFolders: {
    path: "/admin/landing_bucket_folders",
    redirectHandlers: ["MFASetting", "AdminView"],
    routeGroup: "admin",
    element: <PageAdminLandingBucketFolders />,
  },
  adminLandingBucketFolderCreate: {
    path: "/admin/landing_bucket_folders/create",
    redirectHandlers: ["MFASetting", "AdminView"],
    routeGroup: "admin",
    element: <PageAdminLandingBucketFolders mode="create" />,
  },
  adminLandingBucketFolderEdit: {
    path: "/admin/landing_bucket_folders/:folder_id/edit",
    redirectHandlers: ["MFASetting", "AdminView"],
    routeGroup: "admin",
    element: <PageAdminLandingBucketFolders mode="edit" />,
  },
  adminAccountEdit: {
    path: "/admin/accounts/:account_id/edit",
    redirectHandlers: ["MFASetting", "AdminDeveloperView"],
    routeGroup: "admin",
    element: <PageAdminAccountEdit />,
  },
  adminorganizationcreate: {
    path: "/admin/organization_create",
    redirectHandlers: ["MFASetting", "AdminView"],
    routeGroup: "admin",
    element: <PageAdminOrganizationCreate />,
  },
  adminViewMasterCreate: {
    path: "/admin/viewmasters_create",
    redirectHandlers: ["MFASetting", "AdminView"],
    routeGroup: "admin",
    element: <PageAdminViewMasterCreate />,
  },
  organizations: {
    path: "/organizations",
    redirectHandlers: ["MFASetting"],
    element: <PageOrganizations />,
  },
  organizationTop: {
    path: "/organizations/:organization_id",
    redirectHandlers: ["MFASetting", "OrganizationSetting"],
    element: <PageOrganizationTop />,
  },
  organizationSetting: {
    path: "/organizations/:organization_id/organization_setting",
    redirectHandlers: ["MFASetting", "OrganizationSetting"],
    routeGroup: "organizationSetting",
    element: <PageOrganizationSetting tabId="organization_info" />,
  },
  organizationSettingOrganizationInfo: {
    path: "/organizations/:organization_id/organization_setting/organization_info",
    redirectHandlers: ["MFASetting", "OrganizationSetting"],
    routeGroup: "organizationSetting",
    element: <PageOrganizationSetting tabId="organization_info" />,
  },
  organizationSettingFunctions: {
    path: "/organizations/:organization_id/organization_setting/functions",
    redirectHandlers: ["MFASetting", "OrganizationSetting"],
    routeGroup: "organizationSetting",
    element: <PageOrganizationSetting tabId="functions" />,
  },
  organizationSettingMembers: {
    path: "/organizations/:organization_id/organization_setting/members",
    redirectHandlers: ["MFASetting", "OrganizationSetting"],
    routeGroup: "organizationSetting",
    element: <PageOrganizationSetting tabId="members" />,
  },
  organizationSettingMembersEdit: {
    path: "/organizations/:organization_id/organization_setting/members/:member_id/edit",
    redirectHandlers: ["MFASetting", "OrganizationSetting"],
    routeGroup: "organizationSetting",
    element: <PageOrganizationSetting tabId="members" />,
  },
  organizationSettingMembersInvite: {
    path: "/organizations/:organization_id/organization_setting/members/invite",
    redirectHandlers: ["MFASetting", "OrganizationSetting"],
    routeGroup: "organizationSetting",
    element: <PageMembersInvite />,
  },
  organizationSettingVersionCategories: {
    path: "/organizations/:organization_id/organization_setting/version_categories",
    redirectHandlers: ["MFASetting", "OrganizationSetting"],
    routeGroup: "organizationSetting",
    element: <PageOrganizationSetting tabId="version_categories" />,
  },
  versionCategoryEdit: {
    path: "/organizations/:organization_id/organization_setting/version_categories/:version_category_id/edit",
    redirectHandlers: ["MFASetting", "OrganizationSetting"],
    routeGroup: "organizationSetting",
    element: <PageOrganizationSetting tabId="version_categories" />,
  },
  versionCategoryCreate: {
    path: "/organizations/:organization_id/organization_setting/version_categories/create",
    redirectHandlers: ["MFASetting", "OrganizationSetting"],
    routeGroup: "organizationSetting",
    element: <PageOrganizationSetting tabId="version_categories" />,
  },
  versionCategorySort: {
    path: "/organizations/:organization_id/organization_setting/version_categories/sort",
    redirectHandlers: ["MFASetting", "OrganizationSetting"],
    routeGroup: "organizationSetting",
    element: <PageOrganizationSetting tabId="version_categories" />,
  },
  organizationSettingRoles: {
    path: "/organizations/:organization_id/organization_setting/roles",
    redirectHandlers: ["MFASetting", "OrganizationSetting"],
    routeGroup: "organizationSetting",
    element: <PageOrganizationSetting tabId="roles" />,
  },
  organizationSettingRolesEdit: {
    path: "/organizations/:organization_id/organization_setting/roles/:role_id/edit",
    redirectHandlers: ["MFASetting", "OrganizationSetting"],
    routeGroup: "organizationSetting",
    element: <PageOrganizationSetting tabId="roles" />,
  },
  organizationSettingRolesCreate: {
    path: "/organizations/:organization_id/organization_setting/roles/create",
    redirectHandlers: ["MFASetting", "OrganizationSetting"],
    routeGroup: "organizationSetting",
    element: <PageOrganizationSetting tabId="roles" />,
  },
  organizationSettingNotification: {
    path: "/organizations/:organization_id/organization_setting/notification",
    redirectHandlers: ["MFASetting", "OrganizationSetting"],
    routeGroup: "organizationSetting",
    element: <PageOrganizationSetting tabId="notification" />,
  },
  organizationSettingCliApiKeys: {
    path: "/organizations/:organization_id/organization_setting/cli_api_keys",
    redirectHandlers: ["MFASetting", "OrganizationSetting"],
    routeGroup: "organizationSetting",
    element: <PageOrganizationSetting tabId="cli_api_keys" />,
  },
  versionsSummaryComparison: {
    path: "/organizations/:organization_id/versions_summary_comparison",
    redirectHandlers: ["MFASetting", "OrganizationSetting"],
    element: <PageVersionsSummaryComparison />,
  },
  algorithmVersions: {
    path: "/organizations/:organization_id/algorithm_versions",
    redirectHandlers: ["MFASetting", "OrganizationSetting"],
    routeGroup: "algorithmVersion",
    element: <PageAlgorithmVersions />,
  },
  algorithmVersionCreate: {
    path: "/organizations/:organization_id/algorithm_versions/create",
    redirectHandlers: ["MFASetting", "OrganizationSetting"],
    routeGroup: "algorithmVersion",
    element: <PageAlgorithmVersionCreate />,
  },
  algorithmVersionEdit: {
    path: "/organizations/:organization_id/algorithm_versions/:algorithm_version_id/algorithm_version_edit",
    redirectHandlers: ["MFASetting", "OrganizationSetting"],
    routeGroup: "algorithmVersion",
    element: <PageAlgorithmVersionEdit />,
  },
  frontendAddons: {
    path: "/organizations/:organization_id/frontend_addons",
    redirectHandlers: ["MFASetting", "OrganizationSetting"],
    routeGroup: "frontendAddon",
    element: <PageFrontendAddons />,
  },
  frontendAddonCreate: {
    path: "/organizations/:organization_id/frontend_addons/create",
    redirectHandlers: ["MFASetting", "OrganizationSetting"],
    routeGroup: "frontendAddon",
    element: <PageFrontendAddonCreate />,
  },
  frontendAddonEdit: {
    path: "/organizations/:organization_id/frontend_addons/:frontend_addon_id/frontend_addon_edit",
    redirectHandlers: ["MFASetting", "OrganizationSetting"],
    routeGroup: "frontendAddon",
    element: <PageFrontendAddonEdit />,
  },
  frontendAddonVersionUpdate: {
    path: "/organizations/:organization_id/frontend_addons/:frontend_addon_id/frontend_addon_version_update",
    redirectHandlers: ["MFASetting", "OrganizationSetting"],
    routeGroup: "frontendAddon",
    element: <PageFrontendAddonVersionUpdate />,
  },
  tadSheets: {
    path: "/organizations/:organization_id/tad_sheets",
    redirectHandlers: ["MFASetting", "OrganizationSetting"],
    routeGroup: "tadSheet",
    element: <PageTadSheets />,
  },
  tadSheetCreate: {
    path: "/organizations/:organization_id/tad_sheets/create",
    redirectHandlers: ["MFASetting", "OrganizationSetting"],
    routeGroup: "tadSheet",
    element: <PageTadSheetCreate />,
  },
  tadSheetSetting: {
    path: "/organizations/:organization_id/tad_sheets/:tad_sheet_id/edit",
    redirectHandlers: ["MFASetting", "OrganizationSetting"],
    routeGroup: "tadSheet",
    element: <PageTadSheetSetting />,
  },
  versionTop: {
    path: "/organizations/:organization_id/versions/:version_id",
    searchParams: {
      tab: {
        key: "activeTab",
        values: { views: "views", sheets: "sheets" },
      },
    },
    redirectHandlers: ["MFASetting", "OrganizationSetting"],
    element: <PageVersionTop />,
  },
  versionCreate: {
    path: "/organizations/:organization_id/versions/create",
    redirectHandlers: ["MFASetting", "OrganizationSetting", "EnableSheet"],
    element: <PageVersionCreate />,
  },
  versionInfo: {
    path: "/organizations/:organization_id/versions/:version_id/version_info",
    redirectHandlers: ["MFASetting", "OrganizationSetting"],
    element: <PageVersionInfo />,
  },
  sheet: {
    path: "/organizations/:organization_id/versions/:version_id/sheets/:sheet_id",
    redirectHandlers: [
      "MFASetting",
      "OrganizationSetting",
      "EnableSheet",
      "LockVersionOperation",
    ],
    element: <PageSheet />,
  },
  sheetCreate: {
    path: "/organizations/:organization_id/versions/:version_id/sheet_create",
    redirectHandlers: [
      "MFASetting",
      "OrganizationSetting",
      "EnableSheet",
      "LockVersionOperation",
    ],
    element: <PageSheetCreate />,
  },
  extraPage: {
    path: "/organizations/:organization_id/extra_pages/:extra_page_id",
    redirectHandlers: ["MFASetting", "OrganizationSetting", "EnableSheet"],
    element: (
      <ViewContextProvider>
        <PageExtraPage />
      </ViewContextProvider>
    ),
  },
  view: {
    path: "/organizations/:organization_id/versions/:version_id/views/:view_id",
    redirectHandlers: [
      "MFASetting",
      "OrganizationSetting",
      "EnableSheet",
      "LockVersionOperation",
    ],
    element: (
      <ViewContextProvider>
        <PageView />
      </ViewContextProvider>
    ),
  },
  viewEdit: {
    path: "/organizations/:organization_id/versions/:version_id/views/:view_id/edit",
    searchParams: {
      gobacktoviewaftercancel: {
        key: "gobacktoviewafercancel",
        values: { true: "true" },
      },
    },
    redirectHandlers: [
      "MFASetting",
      "OrganizationSetting",
      "EnableSheet",
      "LockVersionOperation",
    ],
    element: <PageViewEdit />,
  },
  uploadSheetSchema: {
    path: "/organizations/:organization_id/versions/upload_sheet_schema",
    redirectHandlers: ["MFASetting", "OrganizationSetting", "EnableSheet"],
    element: <PageUploadSheetSchema />,
  },
  uploadView: {
    path: "/organizations/:organization_id/versions/:version_id/upload_views",
    redirectHandlers: [
      "MFASetting",
      "OrganizationSetting",
      "EnableSheet",
      "LockVersionOperation",
    ],
    element: <PageUploadViews />,
  },
  connects: {
    path: "/organizations/:organization_id/connects",
    redirectHandlers: ["MFASetting", "OrganizationSetting"],
    routeGroup: "connect",
    element: <PageConnects />,
  },
  connectMasters: {
    path: "/organizations/:organization_id/connect_masters",
    redirectHandlers: ["MFASetting", "OrganizationSetting"],
    routeGroup: "connect",
    element: <PageConnectMasters />,
  },
  connectMasterDetail: {
    path: "/organizations/:organization_id/connect_masters/:connect_master_id",
    redirectHandlers: ["MFASetting", "OrganizationSetting"],
    routeGroup: "connect",
    element: <PageConnectMasterDetail />,
  },
  connectMasterEdit: {
    path: "/organizations/:organization_id/connect_masters/:connect_master_id/edit",
    redirectHandlers: ["MFASetting", "OrganizationSetting"],
    routeGroup: "connect",
    element: <PageConnectMasterEdit />,
  },
  connectMasterCreate: {
    path: "/organizations/:organization_id/connect_masters/create",
    redirectHandlers: ["MFASetting", "OrganizationSetting"],
    routeGroup: "connect",
    element: <PageConnectMasterCreate />,
  },
  endpointAddonMasters: {
    path: "/organizations/:organization_id/endpoint_addon_masters",
    redirectHandlers: ["MFASetting", "OrganizationSetting"],
    routeGroup: "endpointAddon",
    element: <PageEndpointAddonMasters />,
  },
  endpointAddonMasterEdit: {
    path: "/organizations/:organization_id/endpoint_addon_masters/:endpoint_addon_master_id/edit",
    redirectHandlers: ["MFASetting", "OrganizationSetting"],
    routeGroup: "endpointAddon",
    element: <PageEndpointAddonMasterEdit />,
  },
  endpointAddonMasterCreate: {
    path: "/organizations/:organization_id/endpoint_addon_masters/create",
    redirectHandlers: ["MFASetting", "OrganizationSetting"],
    routeGroup: "endpointAddon",
    element: <PageEndpointAddonMasterCreate />,
  },
  endpointAddons: {
    path: "/organizations/:organization_id/endpoint_addons",
    redirectHandlers: ["MFASetting", "OrganizationSetting"],
    routeGroup: "endpointAddon",
    element: <PageEndpointAddons />,
  },
  endpointAddonCreate: {
    path: "/organizations/:organization_id/endpoint_addons/create",
    redirectHandlers: ["MFASetting", "OrganizationSetting"],
    routeGroup: "endpointAddon",
    element: <PageEndpointAddonCreate />,
  },
  endpointAddonSetting: {
    path: "/organizations/:organization_id/endpoint_addons/:endpoint_addon_id/edit",
    redirectHandlers: ["MFASetting", "OrganizationSetting"],
    routeGroup: "endpointAddon",
    element: <PageEndpointAddonSetting />,
  },
  connectSetting: {
    path: "/organizations/:organization_id/connects/:connect_id",
    redirectHandlers: ["MFASetting", "OrganizationSetting"],
    routeGroup: "connect",
    element: <PageConnectSetting />,
  },
  connectCreate: {
    path: "/organizations/:organization_id/connect_create",
    redirectHandlers: ["MFASetting", "OrganizationSetting"],
    routeGroup: "connect",
    element: <PageConnectCreate />,
  },
} as const satisfies AppRoutesDefine;

const applyInitialOrganizationSettingRedirectHandler = (element: ReactNode) => {
  return (
    <InitialOrganizationSettingRedirectHandler>
      {element}
    </InitialOrganizationSettingRedirectHandler>
  );
};

const applyInitialMFASettingRedirectHandler = (element: ReactNode) => {
  return (
    <InitialMFASettingRedirectHandler>
      {element}
    </InitialMFASettingRedirectHandler>
  );
};

const applyEnableSheetRedirectHandler = (element: ReactNode) => {
  return <EnableSheetRedirectHandler>{element}</EnableSheetRedirectHandler>;
};

const applyLockVersionOperationRedirectHandler = (element: ReactNode) => {
  return (
    <LockVersionOperationRedirectHandler>
      {element}
    </LockVersionOperationRedirectHandler>
  );
};

const applyAdminViewRedirectHandler = (element: ReactNode) => {
  return <AdminViewRedirectHandler>{element}</AdminViewRedirectHandler>;
};

const applyAdminDeveloperViewRedirectHandler = (element: ReactNode) => {
  return (
    <AdminDeveloperViewRedirectHandler>
      {element}
    </AdminDeveloperViewRedirectHandler>
  );
};

const applyRedirectHandler = (
  element: ReactNode,
  redirectHandlers?: readonly RedirectHandlerType[]
) => {
  let el = element;
  // NOTE: コンポーネントは外側から評価されるので、評価の順番の逆でapplyする
  if (redirectHandlers?.includes("LockVersionOperation")) {
    el = applyLockVersionOperationRedirectHandler(el);
  }
  if (redirectHandlers?.includes("EnableSheet")) {
    el = applyEnableSheetRedirectHandler(el);
  }
  if (redirectHandlers?.includes("AdminView")) {
    el = applyAdminViewRedirectHandler(el);
  }
  if (redirectHandlers?.includes("AdminDeveloperView")) {
    el = applyAdminDeveloperViewRedirectHandler(el);
  }
  if (redirectHandlers?.includes("MFASetting")) {
    el = applyInitialMFASettingRedirectHandler(el);
  }
  if (redirectHandlers?.includes("OrganizationSetting")) {
    el = applyInitialOrganizationSettingRedirectHandler(el);
  }

  return el;
};

export type AppRouteId = keyof typeof appRoutesDefine;
const routes = Object.entries(appRoutesDefine as AppRoutesDefine).map(
  ([routeId, { path, element, redirectHandlers, routeGroup }]) => ({
    routeIdCtxValue: { routeId: routeId as AppRouteId, routeGroup },
    path,
    isAdminView: routeGroup === "admin",
    element: applyRedirectHandler(element, redirectHandlers),
  })
);

const routeIdCtx = createContext<{
  routeId: AppRouteId;
  routeGroup?: AppRouteGroup;
}>({
  routeId: "initialSetting",
});
export const useAppRouteId = (): AppRouteId => {
  return useContext(routeIdCtx).routeId;
};
export const useAppRouteGroup = (): AppRouteGroup | undefined => {
  return useContext(routeIdCtx).routeGroup;
};

const invisibleSideNavRouteIds: AppRouteId[] = ["initialSetting"];

export const AppRoutes: React.FC = () => {
  const { isAAAdminOrAbove, isAADeveloperOrAbove } = useAppAccount();
  return (
    <Routes>
      <Route path="/" element={<RedirectToActualTop />} />
      {routes.map(({ routeIdCtxValue, path, element, isAdminView }) => {
        const isAdminMode = isAdminView && isAADeveloperOrAbove;
        const isInOrgRoute = path.startsWith("/organizations/");
        const Provider = isAdminMode
          ? isAAAdminOrAbove
            ? AdminProvider
            : AdminDeveloperProvider
          : isInOrgRoute
            ? InOrganizationProvider
            : React.Fragment;
        const isVisibleSideNav = !invisibleSideNavRouteIds.includes(
          routeIdCtxValue.routeId as AppRouteId
        );

        return (
          <Route
            key={path}
            path={path}
            element={
              <routeIdCtx.Provider value={routeIdCtxValue}>
                <Provider>
                  <AppBaseLayout
                    isVisibleSideNav={isVisibleSideNav}
                    isAdminMode={isAdminMode}
                  >
                    {element}
                  </AppBaseLayout>
                </Provider>
                <DatadogViewDispatcher />
              </routeIdCtx.Provider>
            }
          />
        );
      })}
      {/* 未知 URL の 404。組織配下の未知 URL (/organizations/<org>/<未知>) もこの
          catch-all に来るが、ここは InOrganizationProvider 配下ではないため、組織配下
          pathname だと組織ロード完了まで return してしまう AppBaseLayout は使えず
          (空白画面になる)。Provider 上位階層でも安全に描画でき、組織未検出時
          (AppOrgsAndInvitedOrgsProvider) の notFound と描画も揃う TopNavOnlyLayout で
          ヘッダー (アカウント設定 = サインアウト動線) 付きの 404 を表示する。 */}
      <Route
        path="*"
        element={
          <TopNavOnlyLayout>
            <ErrorDisplay errorDisplayType="notFound" />
          </TopNavOnlyLayout>
        }
      />
    </Routes>
  );
};
